"use client";
import Button from "../Button";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useI18N, usePostSaveVenue, useSavedPlaces } from "common/hooks";
import {
  savedPlaceIdToViewAtom,
  savedPlaceIdToViewDataChangedAtom,
} from "@/state/atoms";
import { useEffect, useRef, useState } from "react";
import { bucketUrlPrefix } from "common/config";
import AnalyticsService from "@/services/analytics";
import { useAtom } from "jotai";
import Loader from "../Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";

interface AddRemoveVenueToListProps {
  onClickCreateNew: () => void;
  onClose: () => void;
  businessId: number;
  venueDetails: any;
  listNameCreated: string;
}

const AddRemoveVenueToList = ({
  onClickCreateNew,
  onClose,
  businessId,
  venueDetails,
  listNameCreated,
}: AddRemoveVenueToListProps) => {
  const isDark = useDarkMode();
  const i18n = useI18N();

  const showToast = useCustomToast();

  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceIdToViewDataChanged] = useAtom(
    savedPlaceIdToViewDataChangedAtom
  );

  const { data, refetch } = useSavedPlaces();
  const { addToLists, error, isLoading, result } = usePostSaveVenue();
  const [selectedList, setSelectedList] = useState<number[]>([]);

  useEffect(() => {
    if (data && data?.saved_places) {
      // console.log("data", data);
      const newList = [];

      data.saved_places.forEach((item) => {
        // console.log("item", item);
        item?.saved_place_details.forEach((innerItem) => {
          if (innerItem.business_id === businessId) {
            newList.push(innerItem.saved_place_id);
          }
        });

        // console.log("listNameCreated", listNameCreated, (listNameCreated === item.name));
        if(listNameCreated === item.name){
          newList.push(item.id);
        }
      });

      setSelectedList(newList);
    }
  }, [listNameCreated, businessId, data]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler(
        "mySavedPlacesErrorSubmittingAddToSavedPlaces"
      );
      AnalyticsService.trackMx("mySavedPlacesErrorSubmittingAddToSavedPlaces", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (result) {
      console.log("in result savedPlaceIdToView", savedPlaceIdToView);

      AnalyticsService.trackGAEventHandler("mySavedPlacesClickedSaveToList");
      AnalyticsService.trackMx("mySavedPlacesClickedSaveToList", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      // console.log("result", result);
      showToast(result.message, "success");
      refetch();

      if (savedPlaceIdToView) {
        // reload map
        console.log("reload map");
        setSavedPlaceIdToViewDataChanged(true);
      }

      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, i18n]);

  const handleSelectedList = (id: number) => {
    if (selectedList.includes(id)) {
      AnalyticsService.trackGAEventHandler("mySavedPlacesVenueRemovedFromList");
      AnalyticsService.trackMx("mySavedPlacesVenueRemovedFromList", {});

      setSelectedList(selectedList.filter((item) => item !== id));
    } else {
      AnalyticsService.trackGAEventHandler("mySavedPlacesVenueAddedToList");
      AnalyticsService.trackMx("mySavedPlacesVenueAddedToList", {});

      setSelectedList([...selectedList, id]);
    }
  };

  // Function to handle image load failure
  const placeHolderImage = "/images/business_default_image.svg";
  const handleImageLoadError = (event) => {
    event.target.src = placeHolderImage;
    event.target.style = "object-fit:contain";
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div>
      {isLoading && <Loader />}

      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-200px)] overflow-y-auto z-1100"
        // role="none"
        role="dialog" aria-label={i18n.get("addToSavedPlaces")} ref={firstFocusRef}  tabIndex={0}
      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            // tabIndex={0}
            // ref={firstFocusRef} // Attach the ref here
          >
            {i18n.get("addToSavedPlaces")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3 px-3"
          role="none"
        >
          <div className="px-4 md:px-12 mt-6 flex flex-col gap-8">
            <label
              role="button"
              className="flex items-center gap-6 "
              onClick={onClickCreateNew}
              tabIndex={0}
              aria-label={i18n.get("createANewList")}
            >
              <img
                src={
                  isDark
                    ? "/images/CreateSavedPlaceIcon_dark.svg"
                    : "/images/createListBox.svg"
                }
                alt={i18n.get("createANewList")}
                className="object-cover w-[104px] h-[98px] rounded-xl"
                aria-hidden="true"
              />
              <div
                className="paragraph-medium-semi-bold  text-primary-800 dark:text-primary-150"
                aria-hidden="true"
              >
                {i18n.get("createANewList")}
              </div>
            </label>

            {data?.saved_places.map((item, index) => (
              <div
                key={index}
                className="cursor-pointer flex items-center gap-6"
                onClick={() => {
                  handleSelectedList(item.id);
                }}
                tabIndex={0}
                aria-label={selectedList.includes(item.id) ? `${item.name} selected` : `${item.name}`}  

              >
                <div className={listNameCreated === item.name ? "p-1 rounded-lg border-2 border-primary-500" : ""}> 
                  {item.cover_photo ? (
                    <img
                      src={bucketUrlPrefix + item.cover_photo}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[98px]"
                      onError={handleImageLoadError}
                      aria-hidden="true"
                    />
                  ) : item.saved_place_details.length > 0 &&
                    item.saved_place_details[0].photo_thumb != "" &&
                    item.saved_place_details[0].photo_thumb != null ? (
                    <img
                      src={
                        bucketUrlPrefix + item.saved_place_details[0].photo_thumb
                      }
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[98px]"
                      onError={handleImageLoadError}
                      aria-hidden="true"
                    />
                  ) : (
                    <img
                      src={placeHolderImage}
                      alt={item.name}
                      className="rounded-lg w-[104px] h-[98px]"
                      onError={handleImageLoadError}
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className="flex-grow">
                  <div className="paragraph-medium-semi-bold text-primary-800 dark:text-primary-150">
                    {item.name}
                  </div>
                  <div className="paragraph-small-semibold text-primary-500 dark:text-neutral-400">
                    {item.total_places.toString() + " place saved"}
                  </div>

                  {listNameCreated === item.name && (
                    <div className="h-6 px-4 py-1 bg-primary-400 rounded-[20px] justify-start items-start inline-flex">
                      <div className="paragraph-xsmall-semibold text-primary-10 dark:text-primary-10">New List</div>
                    </div>
                  )}

                </div>
                <div>
                  {selectedList.includes(item.id) ? (
                    <img
                      src={
                        isDark
                          ? "/images/CheckCircle.svg"
                          : "/images/CheckCircle.svg"
                      }
                      alt="List selected"
                      className="w-[32px] h-[32px]"
                    />
                  ) : (
                    <img
                      src={
                        isDark
                          ? "/images/PlusCircle_dark.svg"
                          : "/images/PlusCircle.svg"
                      }
                      alt="List not selected"
                      className="w-[32px] h-[32px]"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="w-96 h-28 px-16 py-4 bg-white dark:bg-primary-900 flex-col justify-center items-center gap-4 inline-flex">
            <div className="w-96 justify-center items-center inline-flex">
              <Button
                className="grow"
                //  disabled={isLoading}
                onClick={() => {
                  console.log("ssubmit clicked", businessId);

                  if (!businessId) return;

                  addToLists({
                    listIds: selectedList,
                    venueId: businessId,
                  });
                }}
              >
                {i18n.get("save")}
              </Button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 "></div>
        </footer>
        <button
          onClick={onClose}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </div>
  );
};

export default AddRemoveVenueToList;
